import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { meQueryKeys, platformApi } from "../api/platform";
import { resendVerificationEmail } from "../api/auth";

/**
 * Persistent "waiting for e-mail activation" banner shown across the app shell while the logged-in
 * owner's account is unverified (they registered but haven't clicked the activation link yet). Reads
 * the authoritative flag from `/me` so it survives reloads and clears itself once the link is
 * clicked (the verify page invalidates this query). Renders nothing when verified.
 */
export function EmailVerifyBanner() {
  const { data: me } = useQuery({
    queryKey: meQueryKeys.self(),
    queryFn: () => platformApi.me(),
    staleTime: 60 * 1000,
  });
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  // `emailVerified` is optional on older responses — only show the banner when explicitly false.
  if (!me || me.emailVerified !== false) {
    return null;
  }

  async function onResend() {
    setResending(true);
    try {
      await resendVerificationEmail();
      setResent(true);
    } finally {
      setResending(false);
    }
  }

  return (
    <div
      role="status"
      className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800"
    >
      <span>
        <strong className="font-semibold">Chưa kích hoạt email.</strong> Vui lòng kiểm tra hộp thư và
        nhấn liên kết kích hoạt để xác nhận tài khoản.
      </span>
      <span>
        {resent ? (
          <span>Đã gửi lại email ✓</span>
        ) : (
          <button
            type="button"
            onClick={onResend}
            disabled={resending}
            className="rounded-md border border-amber-400 bg-white px-3 py-1 font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            {resending ? "Đang gửi…" : "Gửi lại email"}
          </button>
        )}
      </span>
    </div>
  );
}
